import { useEffect, useRef, useCallback } from "react";
import type { Router } from "expo-router";

import { useQueryClient } from "@tanstack/react-query";

import type { DeviceTokenApi } from "./device-token-api";
import {
  onTokenRefresh,
  onForegroundMessage,
  getInitialNotification,
  onNotificationOpenedApp,
  getDevicePlatform,
} from "./messaging-service";
import type { RemoteMessage } from "./messaging-service";
import { navigateToNotification } from "./navigate-to-notification";
import { useDeviceToken, STATUS_QUERY_KEY } from "./use-device-token";

export interface UsePushNotificationsOptions {
  auth: { isAuthenticated: boolean; isInitialized: boolean };
  router: Router;
  showForegroundToast: (title: string, body?: string) => void;
  deviceTokenApi: DeviceTokenApi;
  /**
   * When false, skips token/device registration and notification listeners (e.g. product feature flag).
   * @default true
   */
  enabled?: boolean;
}

/**
 * Manages the full push notification lifecycle: permission, token registration,
 * foreground display, and notification-tap deep linking.
 *
 * All app-specific dependencies (auth state, router, toast, API client) are
 * injected via the options object so this hook can live in the feature package.
 *
 * Async state (permission check, token lookup, backend registration check) is
 * handled by React Query via `useDeviceToken`. Subscriptions are pure
 * synchronous effects gated on the query result.
 */
export function usePushNotifications({
  auth,
  router,
  showForegroundToast,
  deviceTokenApi,
  enabled: featureEnabled = true,
}: UsePushNotificationsOptions) {
  const { isAuthenticated, isInitialized } = auth;
  const queryClient = useQueryClient();
  const pendingDeepLinkRef = useRef<RemoteMessage | null>(null);
  const initialNotificationHandledRef = useRef(false);
  const handleTapRef = useRef<(message: RemoteMessage) => void>(undefined);

  const { isRegistered } = useDeviceToken({
    deviceTokenApi,
    enabled: featureEnabled && isAuthenticated && isInitialized,
  });
  const registered = isRegistered.data?.registered ?? false;

  const registerToken = useCallback(
    async (fcmToken: string) => {
      try {
        const platform = getDevicePlatform();
        await deviceTokenApi.registerToken(fcmToken, platform);
        void queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
      } catch (error) {
        console.error("[Push] Failed to register token:", error);
      }
    },
    [deviceTokenApi, queryClient],
  );

  const navigate = useCallback((message: RemoteMessage) => navigateToNotification(router, message), [router]);

  // Keep the tap handler ref current so the mount-once effect below always
  // calls through to the latest closure without re-subscribing.
  handleTapRef.current = (message: RemoteMessage) => {
    if (!isAuthenticated || !isInitialized) {
      pendingDeepLinkRef.current = message;
      return;
    }
    navigate(message);
  };

  // Notification tap handling — subscribes once on mount.
  // Uses handleTapRef so the listener always sees current auth state.
  useEffect(() => {
    if (!featureEnabled) return;

    if (!initialNotificationHandledRef.current) {
      initialNotificationHandledRef.current = true;
      void getInitialNotification().then((message) => {
        if (message) handleTapRef.current?.(message);
      });
    }

    const unsubscribe = onNotificationOpenedApp((message) => {
      handleTapRef.current?.(message);
    });

    return unsubscribe;
  }, [featureEnabled]);

  // Drain any deep link that arrived before auth was ready.
  useEffect(() => {
    if (!featureEnabled) return;
    if (!isAuthenticated || !isInitialized) return;

    const pending = pendingDeepLinkRef.current;
    if (pending) {
      pendingDeepLinkRef.current = null;
      navigate(pending);
    }
  }, [featureEnabled, isAuthenticated, isInitialized, navigate]);

  // Token refresh and foreground message listeners — only active while the
  // device token is registered on the backend. When the settings screen
  // toggles registration, React Query invalidates the status query, flipping
  // `registered` and causing this effect to subscribe/unsubscribe cleanly.
  useEffect(() => {
    if (!featureEnabled || !registered) return;

    const unsubRefresh = onTokenRefresh((newToken) => {
      void registerToken(newToken);
    });

    const unsubForeground = onForegroundMessage((message) => {
      const title = message.notification?.title ?? "Notification";
      const body = message.notification?.body;
      showForegroundToast(title, body);
    });

    return () => {
      unsubRefresh();
      unsubForeground();
    };
  }, [featureEnabled, registered, registerToken, showForegroundToast]);
}
